<template>
    <div class="relative flex h-dvh flex-col gap-4 lg:h-full">
        <FormKit
            v-slot="{ state: { valid } }"
            type="form"
            :actions="false"
            form-class="flex h-dvh flex-col justify-between rounded-md border-2 border-gray-200 lg:h-full"
            @submit="submitCart()"
        >
            <div class="relative flex flex-col gap-4">
                <transition name="fade">
                    <div v-if="cart().cartIsLoading" class="absolute inset-0 z-20 flex cursor-not-allowed flex-col items-center justify-center gap-2 overflow-hidden bg-white/80">
                        <Icon :icon="IconLoading" class="absolute right-8 top-8 size-4 animate-spin" />
                    </div>
                </transition>
                <CartTitle />
                <CartToggleButton class="px-6" />
                <div class="relative">
                    <div
                        ref="scrollContainer"
                        class="relative flex h-dvh flex-col overflow-auto lg:h-full lg:max-h-[calc(100vh-31rem)]"
                        :class="!webshopSettings().hasMultipleDispatchTypes ? 'max-h-[calc(100dvh-17rem)] sm:max-h-[calc(100dvh-13rem)]' : 'max-h-[calc(100dvh-20.5rem)] sm:max-h-[calc(100dvh-16rem)]'"
                    >
                        <div class="flex px-6 pb-6 md:hidden">
                            <OrderNotifications />
                        </div>
                        <div class="flex flex-col gap-4 p-6 pt-0">
                            <FormKit type="group" name="cart">
                                <TransitionGroup name="card-list-transition">
                                    <template v-if="cart().hasCartProducts">
                                        <TransitionGroup :name="cart().cartItemsShouldAnimate ? 'fade-scale' : null" tag="ul" class="flex flex-col space-y-4 divide-y divide-solid" mode="out-in">
                                            <li v-for="(cartProduct, productIndex) in cart().getCartProducts" :key="productIndex" class="relative flex w-full gap-4 bg-white pt-4 first:pt-0">
                                                <CartProductCard :cart-product="cartProduct" :cart-product-index="productIndex" />
                                            </li>
                                        </TransitionGroup>
                                    </template>
                                    <p v-else class="items-center">
                                        {{ $t('cart.cartEmpty') }}.
                                    </p>
                                </transitiongroup>
                            </FormKit>
                        </div>
                        <AdditionalCost v-if="cart().hasCartProducts" :additional-cost="cart().getAdditionalCostsVerified" />
                        <CartDiscounts />
                        <CartLoyaltyPoints v-if="hasLoyaltyPoints" />
                        <div class="mb-6 flex px-6 md:hidden">
                            <OrderNotifications />
                        </div>
                    </div>
                </div>
            </div>
            <CartTotal :disabled="!valid" />
        </FormKit>
    </div>
</template>

<script setup lang="ts">
    import { useIdle, watchDebounced } from '@vueuse/core'
    import { useVfm } from 'vue-final-modal'
    import IconLoading from '@/assets/icons/loading.svg'
    import AdditionalCost from '~/components/additionalCost.vue'
    import CartDiscounts from '~/components/Cart/CartDiscounts.vue'
    import CartLoyaltyPoints from '~/components/Cart/CartLoyaltyPoints.vue'
    import CartProductCard from '~/components/Cart/CartProductCard.vue'
    import CartTitle from '~/components/Cart/CartTitle.vue'
    import CartToggleButton from '~/components/Cart/CartToggleButton.vue'
    import CartTotal from '~/components/Cart/CartTotal.vue'
    import Icon from '~/components/Icon.vue'
    import OrderNotifications from '~/components/OrderNotifications.vue'
    import { cart } from '~/store/cart'
    import { webshopSettings } from '~/store/webshopSettings'
    import { isCheckoutPath } from '~/utils/path-utils'
    const hasLoyaltyPoints = false
    const { idle } = useIdle(300)
    const scrollContainer = useTemplateRef('scrollContainer')
    const { $listen, $destroy } = useNuxtApp()
    // Fetch the calculator prices and sync the cart when the component is initially mounted
    onMounted(async () => {
        await nextTick()
        await cart().removeUnavailableCartProducts()
        await cart().updateLocalCartPrices()
        await cart().updateValidatedCartPrices()
        scrollToBottom()
        $listen('before-new-product-added-to-cart', () => {
            scrollToBottom()
        })
    })
    onBeforeUnmount(() => {
        $destroy('before-new-product-added-to-cart')
    })
    function scrollToBottom () {
        scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight
    }
    // Sync the cart when the user is idle
    watch(idle, async userIsIdle => {
        if (userIsIdle) {
            await cart().updateValidatedCartPrices()
        }
    })
    // Update the calculator prices when the cart changes
    watchDebounced(cart()?.cart, async () => {
        await cart().updateLocalCartPrices()
    }, { deep: true, debounce: 1000, maxWait: 5000 })
    // Recalculate the cart an extra time after the calculated price has changed. This is necessary because the tip is calculated based on the cart price, and we want to show the tip to the user before that already. See https://foodticket.atlassian.net/browse/CV-11 for more context.
    watch(() => cart().getCartTotalPriceLocal, async () => {
        await nextTick()
        await cart().updateLocalCartPrices()
    })
    const vfm = useVfm()
    const { closeAll } = vfm
    const localePath = useLocalePath()
    const route = useRoute()
    function submitCart () {
        closeAll()
        if (!isCheckoutPath(route.path)) {
            navigateTo(localePath('/checkout'))
        }
    }
</script>

<style lang="scss" scoped>
.card-list-transition-enter-active{
    @apply transition-all duration-300;
}
.card-list-transition-enter-from,
.card-list-transition-leave-to {
    @apply opacity-0 scale-95;
}
</style>
